import {
  resolveSelection,
  SelectionState,
} from "@monolith-forensics/monolith-ui";
import { useMemo, useRef, useState } from "react";

import { isSelectableContact } from "../contacts.helpers";
import { Contact } from "../contacts.types";
import {
  demoteFalseSelectAll,
  deselectKey,
  EMPTY_SELECTION,
  materializeSelection,
} from "./ContactsList.selection";

/** Imperative surface for callers that need to change the selection themselves. */
export interface ContactsListHandle {
  /** Unticks a single contact, e.g. from a pill the caller renders elsewhere. */
  deselect: (clientId: string | number) => void;
  clearSelection: () => void;
}

interface UseContactSelectionOptions {
  /** The contacts currently loaded, in list order. */
  contacts: Contact[];
  /** The server-wide record count for the current query, when it is known. */
  total?: number;
}

export interface ContactSelection {
  selectionState: SelectionState;
  /** The whole Contact objects the selection currently resolves to. */
  selectedContacts: Contact[];
  /** Wire to List's `onSelectionChange`. */
  handleSelectionChange: (next: SelectionState) => void;
  /**
   * Pins a blanket selection to concrete keys. Call it immediately before the
   * query behind `contacts` changes — see `materializeSelection` for why.
   */
  pinSelection: () => void;
  handle: ContactsListHandle;
}

/**
 * The stateful half of the picker's selection: the durable key index, the
 * resolved contacts, and the handlers that keep a blanket selection from
 * outliving the query it was made against.
 *
 * The decisions themselves live in ContactsList.selection.ts, which is pure and
 * tested. What is left here is React — refs, memos, and the ordering between
 * them — kept out of the component so the component is only its markup.
 */
export const useContactSelection = ({
  contacts,
  total,
}: UseContactSelectionOptions): ContactSelection => {
  const [selectionState, setSelectionState] =
    useState<SelectionState>(EMPTY_SELECTION);

  /*
    Durable key -> contact index. A selection outlives the pages backing it — a
    new search drops rows the user already ticked — and callers need the whole
    Contact, so keep every contact we have seen rather than only the loaded set.
  */
  const contactIndex = useRef(new Map<string, Contact>());

  /*
    Indexing rides along with the keys because both walk the same list, and
    because this has to happen before `selectedContacts` reads the index below.
    Hooks run top to bottom within a render, so declaring it here is the
    ordering guarantee.
  */
  const loadedKeys = useMemo(() => {
    const keys = new Set<string>();

    contacts.forEach((contact) => {
      const key = String(contact.client_id);
      keys.add(key);
      contactIndex.current.set(key, contact);
    });

    return keys;
  }, [contacts]);

  /*
    The inclusion/exclusion model itself lives in monolith-ui; this only tells
    it what to resolve against.

    `items` is the loaded set because Ctrl/Cmd+A marks every row selected,
    including pages fetched after the keypress, so a blanket selection has to
    mirror that rather than trust the key snapshot it took. Reading the loaded
    set is only safe because the handlers below keep a blanket selection from
    ever outliving the query it was made against — anything that would have to
    span queries is held as keys, and `resolveKey` is what lets those keys
    still name a contact a new search has dropped.

    isSelectableContact is applied here as well as through List's own
    `isItemSelectable`, not instead of it: this is what the caller submits, so
    it must hold whatever the selection state says, and a blanket "all" the
    List has not narrowed yet would otherwise hand over every loaded contact
    including the ones already on the case.
  */
  const selectedContacts = useMemo(
    () =>
      resolveSelection(selectionState, {
        items: contacts,
        getKey: (contact) => String(contact.client_id),
        isSelectable: isSelectableContact,
        resolveKey: (key) => contactIndex.current.get(key),
      }),
    [selectionState, contacts]
  );

  /*
    Held in a ref so the handle below need not re-create itself every time the
    selection changes: it only ever reads the selection at call time.
  */
  const selectedContactsRef = useRef(selectedContacts);
  selectedContactsRef.current = selectedContacts;

  /** Same, for the totals `deselectKey` needs but nothing else here reads. */
  const totalSelectableRef = useRef(0);
  totalSelectableRef.current = total ?? contacts.length;

  const handleSelectionChange = (next: SelectionState) => {
    // Everything the List reports goes through the demotion: the promotion it
    // undoes happens on an ordinary tick, not on any one gesture.
    setSelectionState(demoteFalseSelectAll(next, loadedKeys));
  };

  const pinSelection = () =>
    setSelectionState((prev) =>
      materializeSelection(
        prev,
        selectedContactsRef.current.map((contact) => String(contact.client_id))
      )
    );

  /*
    Stable for the life of the picker. Everything it reads is behind a ref, so
    there is nothing here for a dependency list to track — and a handle that
    changed identity would have callers re-wiring it on every selection change.
  */
  const handle = useMemo<ContactsListHandle>(
    () => ({
      deselect: (clientId) => {
        const key = String(clientId);
        const remaining = selectedContactsRef.current.filter(
          (contact) => String(contact.client_id) !== key
        );

        /*
          Pruned as well as read: several deselects can run in one tick — a
          caller unticks every contact a partial submit managed to link — and
          they would all see the same render's snapshot otherwise, so the last
          one would not know it was the last one.
        */
        selectedContactsRef.current = remaining;

        setSelectionState((prev) =>
          deselectKey(
            prev,
            key,
            totalSelectableRef.current,
            remaining.length === 0
          )
        );
      },
      clearSelection: () => setSelectionState(EMPTY_SELECTION),
    }),
    []
  );

  return {
    selectionState,
    selectedContacts,
    handleSelectionChange,
    pinSelection,
    handle,
  };
};
